import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import CommonSelect, { Option } from "../../../core/common/commonSelect";
import { useSelector } from "react-redux";
import { _get, _post } from "../../Utils/Helper";
import { SingleValue } from "react-select";
import { toast } from "react-toastify";
import { RootState } from "../../../redux/reducers";

interface TimetableItem {
  id: number;
  day: string;
  class_name: string;
  subject: string;
  teacher_id: number;
  section: string;
  school_location: string;
  start_time: string;
  end_time: string;
  status: string;
  created_at: string;
  updated_at: string;
  teacher: string;
  activities: string;
  name: string;
}

interface Teacher {
  id: number;
  name: string;
}

interface Subject {
  subject: string;
  section: string;
}

interface TeacherRole {
  class_name: string;
  class_code: string;
  section: string;
}

interface TimetableCardsProps {
  data: TimetableItem[];
  refreshData: () => void;
  section: string;
  class: string;
}

const initialForm = {
  class_name: "",
  section: "",
  subject: "",
  teacher_id: "",
  school_location: "",
  day: "Monday",
  time: "",
  period: "",
  duration: "",
  routinename: "",
  subjectClasses: "",
  query_type: "create" as "create",
  start_time: "",
  end_time: "",
  class_code:'',
};

const initialUpdateForm = {
  id: "",
  class_name: "",
  section: "",
  subject: "",
  teacher_id: "",
  day: "",
  time: "",
  subjectClasses: "",
  query_type: "update" as "update",
  start_time: "",
  end_time: "",
  class_code:"",
};

const TimetableCards: React.FC<TimetableCardsProps> = ({
  data,
  refreshData,
  section,
  class: className,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [form, setForm] = useState(initialForm);
  const [updateForm, setUpdateForm] = useState(initialUpdateForm);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classTiming, setClassTiming] = useState<any[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<TimetableItem | null>(null);
  

  const { teacher_roles, sections,selected_branch } = useSelector((state: any) => state.auth);
  const { user } = useSelector((state: RootState) => state.auth);

  const isTeacher = user?.role?.toLowerCase() === "teacher"; 

  const openAddModal = (timeSlot: string, day: string) => {
    const [start, end] = timeSlot.split("-");
    setSelectedTimeSlot(timeSlot);
    setSelectedDay(day);
    setForm((prev) => ({
      ...prev,
      start_time: start.trim(),
      end_time: end.trim(),
    }));
    setShowAddModal(true);
  };

  const closeAddModal = () => setShowAddModal(false);

  const openDetailsModal = (entry: TimetableItem) => {
    setSelectedEntry(entry);
    setUpdateForm({
      ...initialUpdateForm,
      id: entry.id.toString(),
      class_name: entry.class_name,
      section: entry.section,
      subject: entry.subject,
      teacher_id: entry.teacher_id.toString(),
      day: entry.day,
      start_time: entry.start_time,
      end_time: entry.end_time,
    });
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => setShowDetailsModal(false);

  const handleUpdateSubmit = () => {
    const payload = {
      ...updateForm,
      school_id: user?.school_id,
    };

    _post(
      "lesson_time_table?query_type=update",
      payload,
      (res) => {
        if (res.success) {
          toast.success("Timetable updated successfully!");
          setUpdateForm(initialUpdateForm);
          closeDetailsModal();
          refreshData();
        }
      },
      (err) => {
        console.error("Update error:", err);
        toast.error("An error occurred while updating the timetable.");
      }
    );
  };

  const handleSubmit = () => {
    const payload = {
      ...form,
      school_id: user?.school_id,
      section,
      day: selectedDay,
      start_time: form.start_time,
      end_time: form.end_time,
      branch_id: selected_branch.branch_id
    };

    _post(
      "lesson_time_table?query_type=create",
      payload,
      (res) => {
        if (res.success) {
          toast.success("Timetable submitted successfully!");
          setForm(initialForm);
          closeAddModal();
          refreshData();
        }
      },
      (err) => {
        console.error("Submission error:", err);
        toast.error("An error occurred while submitting the timetable.");
      }
    );
  };

  // const timeToMinutes = (time: string) => {
  //   const [hours, minutes] = time.split(":").map(Number);
  //   return hours * 60 + minutes;
  // };

  const getClassTiming = useCallback(() => {
    if (!isTeacher){
      _get(
        `get_class_timing?query_type=select&section=${section}`,
        (res) => {
          if (res.data && res.data.length) {
            setClassTiming(res.data);
          }
        },
        (err) => {
          toast.error("Error occurred");
          console.error("Error: ", err);
        }
      );
  } else {
    if(section){ _get(
      `get_class_timing?query_type=select&section=${section}`,
      (res) => {
        if (res.data && res.data.length) {
          setClassTiming(res.data);
        }
      },
      (err) => {
        toast.error("Error occurred");
        console.error("Error: ", err);
      }
    );} else {
    _get(
      `get_class_timing?query_type=select&section=${teacher_roles[0]?.section}`,
      (res) => {
        if (res.data && res.data.length) {
          setClassTiming(res.data);
        }
      },
      (err) => {
        toast.error("Error occurred");
        console.error("Error: ", err);
      }
    );}
  }
  }, [section, teacher_roles]);

  useEffect(() => {
    getClassTiming();
  }, [getClassTiming, section, teacher_roles]);

  useEffect(() => {
    setForm((prev) => ({...prev, class_name: className  }));
  }, [className]);

  useEffect(() => {
    _get(
      "teachers?query_type=select-all",
      (res) => {
        setTeachers(res.results);
      },
      (err) => {
        console.log(err);
      }
    );
  }, []);

  useEffect(() => {
    if (form.class_code) {
      _get(
        `get_subjects?query_type=select-section-subjects&class_code=${form.class_code}&school_id=${user?.school_id}`,
        (res) => {
          if (res.success && res.data && res.data.length) {
            setSubjects(res.data);
          }
        },
        (err) => {
          console.error("Error fetching subjects:", err);
        }
      );
    }
  }, [form.class_code]);
  useEffect(() => {
    if (updateForm.class_code) {
      _get(
        `get_subjects?query_type=select-section-subjects&class_code=${updateForm.class_code}&school_id=${user?.school_id}`,
        (res) => {
          if (res.success && res.data && res.data.length) {
            setSubjects(res.data);
          }
        },
        (err) => {
          console.error("Error fetching subjects:", err);
        }
      );
    }
  }, [updateForm.class_code]);

  const teacherOptions = teachers?.map((teacher) => ({
    label: teacher.name,
    value: teacher.id.toString(),
  }));

  const subjectOptions = subjects?.map((subject) => ({
    label: subject.subject,
    value: subject.subject,
  }));

  const classOptions = teacher_roles?.map((role: TeacherRole) => ({
    label: role.class_name,
    value: role.class_code,
    section: role.section,
  }));
  // console.log(teacher_roles[0].section)

  const groupedData = React.useMemo(() => {
    if (!data) return {};
    return data.reduce((acc, entry) => {
      const key = `${entry.start_time} - ${entry.end_time} - ${entry.day}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(entry);
      return acc;
    }, {} as Record<string, TimetableItem[]>);
  }, [data]);

  const timeSlots = Array.from(
    new Set(classTiming?.map((item) => `${item.start_time} - ${item.end_time}`))
  );

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  const colors = [
    "bg-light text-dark",
    "bg-soft-success",
    "bg-soft-warning",
    "bg-soft-danger",
    "bg-soft-info",
  ];
  const startTime = classTiming?.map((start) => (
    {
    label: start.start_time,
    value: start.start_time,
  }
  ))
  const endTime = classTiming?.map((end) => (
    {
      label: end?.end_time,
      value: end?.end_time,
    }
  ))
// console.log(selected_branch)
  

  return (
    <div className="card-body pb-0">
      
      
      {/* {JSON.stringify(groupedData["9:15 AM - 10:00 AM - Monday"] )} */}
      {/* {JSON.stringify(selected_branch)} */}
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Time</th>
              {daysOfWeek?.map((day) => (
                <th key={day}>{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots?.map((timeSlot, idx) => {
              const isBreak = classTiming.some(
                (item) =>
                  `${item.start_time} - ${item.end_time}` === timeSlot &&
                  item.activities?.toLowerCase() === "break"
              );

              return (
                <tr key={idx}>
                  <td>
                    <b>{timeSlot}</b>
                  </td>
                  {daysOfWeek?.map((day, dayIdx) => {
                    const key = `${timeSlot} - ${day}`;
                    const entries = groupedData[key] || [];

                    if (isBreak) {
                      return (
                        <td
                          key={day}
                          className="text-center"
                          style={{ backgroundColor: "orange" }}
                        >
                          <b>Break</b>
                        </td>
                      );
                    }

                    return (
                      <td key={day} className="p-3">
                        {entries.length > 0 ? (
                          <div className="d-flex flex-column gap-2">
                            {entries?.map((entry, index) => (
                              <div
                                key={index}
                                className={`p-2 rounded ${colors[dayIdx % colors.length]} cursor-pointer`}
                                style={{ position: "relative" }}
                              >
                                <p className="mb-1">
                                  <b>Class:</b> {entry.class_name}
                                </p>
                                <p className="mb-1">
                                  <b>Subject:</b> {entry.subject}
                                </p>
                                <p className="mb-1">
                                  <b>Teacher:</b> {entry.name || "TBD"}
                                </p>
                                {/* {JSON.stringify(entry)} */}
                                {!isTeacher && (
                                  <p
                                    className="text-primary cursor-pointer"
                                    onClick={() => openDetailsModal(entry)}
                                    style={{ position: "absolute", top: "5px", right: "5px" }}
                                  >
                                    Edit
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          !isTeacher && (
                            <button
                              onClick={() => openAddModal(timeSlot, day)}
                              className="btn btn-primary btn-sm w-100"
                            >
                              Add
                            </button>
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Timetable Modal */}
      {!isTeacher && (
        <Modal show={showDetailsModal} onHide={closeDetailsModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Timetable</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                {/* {JSON.stringify(updateForm.section)} */}
                <Form.Label>Class Name</Form.Label>
                <CommonSelect
                  options={classOptions}
                  defaultValue={{
                    label: updateForm.class_name,
                    value: updateForm.class_name,
                  }}
                  handleChange={(newValue) =>
                    setUpdateForm((prev) => ({
                      ...prev,
                      class_name: newValue?.label || "",
                      class_code: newValue?.value || "",
                      section: classOptions.find((option:any) => option.value === newValue?.value)?.section || "",
                    }))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Subject</Form.Label>
                <CommonSelect
                  options={subjectOptions}
                  defaultValue={{
                    label: updateForm.subject,
                    value: updateForm.subject,
                  }}
                  handleChange={(newValue: SingleValue<Option>) =>
                    setUpdateForm((prev) => ({
                      ...prev,
                      subject: newValue?.value || "",
                    }))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Teacher</Form.Label>
                <CommonSelect
                  options={teacherOptions}
                  defaultValue={{
                    label: teachers?.find((t) => t.id === parseInt(updateForm.teacher_id))?.name || "",
                    value: updateForm.teacher_id,
                  }}
                  handleChange={(newValue: SingleValue<Option>) =>
                    setUpdateForm((prev) => ({
                      ...prev,
                      teacher_id: newValue?.value || "",
                    }))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Day</Form.Label>
                <Form.Control
                  type="text"
                  value={updateForm.day}
                  onChange={(e) =>
                    setUpdateForm((prev) => ({ ...prev, day: e.target.value }))
                  }
                />
              </Form.Group>
              {/* {JSON.stringify(updateForm.start_time)} */}
              <Form.Group>
                <Form.Label>Start Time</Form.Label>
                <CommonSelect
                  options={startTime}
                  defaultValue={{
                    label: updateForm.start_time,
                    value: updateForm.start_time,
                  }}
                  handleChange={(newValue: SingleValue<Option>) =>
                    setUpdateForm((prev) => ({
                      ...prev,
                      start_time: newValue?.value || "",
                    }))
                  }
                />
                {/* <Form.Control
                  type="time"
                  value={updateForm.start_time}
                  onChange={(e) =>
                    setUpdateForm((prev) => ({ ...prev, start_time: e.target.value }))
                  }
                /> */}
              </Form.Group>
              <Form.Group>
                <Form.Label>End Time</Form.Label>
                <CommonSelect
                  options={endTime}
                  defaultValue={{
                    label: updateForm.end_time,
                    value: updateForm.end_time,
                  }}
                  handleChange={(newValue: SingleValue<Option>) =>
                    setUpdateForm((prev) => ({
                      ...prev,
                      end_time: newValue?.value || "",
                    }))
                  }
                />
                {/* <Form.Control
                  type="time"
                  value={updateForm.end_time}
                  onChange={(e) =>
                    setUpdateForm((prev) => ({ ...prev, end_time: e.target.value }))
                  }
                /> */}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeDetailsModal}>
              Close
            </Button>
            <Button variant="primary" onClick={handleUpdateSubmit}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Add Timetable Modal */}
      {!isTeacher && (
        <Modal show={showAddModal} onHide={closeAddModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Timetable</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Time Slot</Form.Label>
                <Form.Control type="text" value={selectedTimeSlot} readOnly />
              </Form.Group>
              <Form.Group>
                <Form.Label>Day</Form.Label>
                <Form.Control type="text" value={selectedDay} readOnly />
              </Form.Group>
              <Form.Group>
                {/* {JSON.stringify(classOptions)} */}
                <Form.Label>Class Name</Form.Label>
                <CommonSelect
                  options={classOptions}
                  // defaultValue={{
                  //   label: form.class_name,
                  //   value: form.class_name,
                  // }}
                  handleChange={(newValue: SingleValue<Option>) =>
                    setForm((prev) => ({
                      ...prev,
                      class_name: newValue?.label || "",
                      class_code: newValue?.value || "",
                    }))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Subject</Form.Label>
                <CommonSelect
                  options={subjectOptions}
                  handleChange={(newValue: SingleValue<Option>) =>
                    setForm((prev) => ({
                      ...prev,
                      subject: newValue?.value || "",
                    }))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Teacher</Form.Label>
                <CommonSelect
                  options={teacherOptions}
                  handleChange={(newValue: SingleValue<Option>) =>
                    setForm((prev) => ({
                      ...prev,
                      teacher_id: newValue?.value || "",
                    }))
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeAddModal}>
              Close
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default TimetableCards;